import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // Navigation Hook

import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";

import { styled } from "@mui/system";

import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { headers, adminLogin } from "../../utils/utils";

import { jwtDecode } from "jwt-decode";

// Styled components
const GradientBackground = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  padding: theme.spacing(4),
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  maxWidth: "500px",
  width: "100%",
}));

const PlanCard = styled(Paper)(({ theme, selected }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  textAlign: "center",
  cursor: "pointer",
  height: "220px",
  boxShadow: selected
    ? "0 8px 16px rgba(135, 206, 250, 0.7)"
    : "0 4px 8px rgba(135, 206, 250, 0.5)",
  transition: "0.3s",
  backgroundColor: selected ? "orange" : "#fff",
  color: selected && "white",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 8px 16px rgba(135, 206, 250, 0.7)",
  },
}));

const App = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState("register");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    subscription: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const adminHeader = adminLogin();

  const [isEmailPresent, setIsEmailPresent] = React.useState(false);
  const [isUsernamePresent, setIsUsernamePresent] = React.useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [formTouched, setFormTouched] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email format validation regex
    return emailRegex.test(email);
  };

  const handleChange = async (event) => {
    const { name, value } = event.target;

    if (name === "username") {
      // Sanitize the username: convert to lowercase and remove special characters
      const sanitizedUsername = value.toLowerCase().replace(/[^a-z0-9_]/g, "");

      // Update the form data state with the sanitized value
      setFormData((prevData) => ({
        ...prevData,
        [name]: sanitizedUsername,
      }));

      // Check if the username exists asynchronously
      if (sanitizedUsername) {
        const usernameExists = await checkUsernameExists(sanitizedUsername);
        setIsUsernamePresent(usernameExists);
      }
      return;
    }

    if (name === "email") {
      setFormData((prevData) => ({ ...prevData, [name]: value }));

      // Check if the email exists asynchronously
      const emailExists = await checkEmailExists(value);
      setIsEmailPresent(emailExists);
      return;
    }

    if (name === "password") {
      setFormData((prevData) => ({ ...prevData, [name]: value }));

      // Validate the password
      const validationError = value ? validatePassword(value) : "";
      setPasswordError(validationError);
      return;
    }

    // For other fields
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleNext = async () => {
    // Check if all required fields are filled
    if (
      !formData.name ||
      !formData.email ||
      !formData.username ||
      !formData.password
    ) {
      alert("Please fill all the required fields.");
      return;
    }

    setFormTouched(true);
    if (!validateEmail(formData.email)) {
      console.error("Invalid email format");
      return;
    }

    if (isEmailPresent) {
      alert("The email you entered is already in use.");
      return;
    }

    if (isUsernamePresent) {
      alert("The username you entered is already in use.");
      return;
    }

    if (passwordError) {
      alert("Please fix the password requirements.");
      return;
    }

    setCurrentPage("subscription");
  };

  const handlePlanSelect = (plan) => {
    setFormData({ ...formData, subscription: plan });
  };

  const handleConfirm = () => {
    loadRazorpay();
  };

  const loadRazorpay = () => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => openRazorpay();
    document.body.appendChild(script);
  };
  const pricePlan = { Basic: "499", Standard: "999", Premium: "1999" };
  const openRazorpay = () => {
    const options = {
      key: "rzp_test_v0su4mmAdMHRar", // Replace with your Razorpay key_id
      amount: pricePlan[formData.subscription] * 100, // The amount is in paise (100 paise = 1 INR)
      currency: "INR",
      name: "Vyoog Information",
      description: "Test Transaction",
      image: "https://your-logo-url.com/logo.png",
      handler: function (response) {
        paymentSucessful();
      },
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone,
      },
      theme: {
        color: "#3eabaf",
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const paymentSucessful = () => {
    localStorage.clear();
    setIsLoading(true);
    registerApplication();
  };

  const login = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_OAUTH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          username: formData.username,
          password: formData.password,
            client_id: "eVyoog",
        client_secret: "ssFF0O7wX5UlmWECCxjfOmflw4WRSJng",
        grant_type: "password",
        }),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Authentication failed: ${errorMessage}`);
      }

      const data = await response.json();

      localStorage.setItem("vyg_access_token", data.access_token);
      localStorage.setItem("vyg_refresh_token", data.refresh_token);

      const decodedToken = jwtDecode(data.access_token);

      const userName = decodedToken.given_name || "Unknown user";
      localStorage.setItem("vyg_login_user", userName);

      sentMailNotification();
      navigate("/ticket");
      window.location.reload();
    } catch (error) {
      console.error("Error:", error.message);
    }
  };

  const registerApplication = async () => {
    try {
      const { username, password, email } = formData;

      const url = new URL("https://chatbotpj.evyoog.com/speak");
      url.searchParams.append("data1", username);
      url.searchParams.append("data2", password);
      url.searchParams.append("data3", email);

      const response = await fetch(url.toString(), {
        method: "GET", // GET method
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Script output:", result.output);
      console.log("Script error:", result.error);
      login();
      setIsLoading(false);

    } catch (error) {
      console.error("Error calling /speak:", error);
    }

  };

  const sentMailNotification = async () => {
    try {
      // Function to format date as "dd-MM-yyyy"
      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      // Get the current date and format it
      const startDate = formatDate(new Date());

      // Add one month to the current date
      const renewalDate = new Date();
      renewalDate.setMonth(renewalDate.getMonth() + 1);
      const formattedRenewalDate = formatDate(renewalDate);
      console.log(pricePlan[formData.subscription]);
      const response = await fetch(
        `${process.env.REACT_APP_NOTIFICATION}/notification/save?templatePath=/opt/eVyoog/systemconfig/mailTemplate/Ticket/Ticket-Registeration.html`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers(),
          },
          body: JSON.stringify({
            recipient: formData.email,
            message: "",
            type: "EMAIL",
            subject: `Welcome Aboard! Your ${formData.subscription} Subscription is Confirmed`,
            status: "PENDING",
            placeholders: {
              customer: formData.name,
              plan: formData.subscription,
              startDate: startDate,
              renewalDate: formattedRenewalDate,
              amount: pricePlan[formData.subscription],
            },
          }),
        },
      );

      if (!response.ok) {
        console.error("Failed to send notification.");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users?email=${email}`,
        {
          method: "GET",
          headers: await adminHeader,
        },
      );

      if (response.ok) {
        const data = await response.json();
        const userExists = data.some((user) => user.email === email);

        if (userExists) return true;
        else return false;
      } else {
        console.error("Failed to check email");
        return false;
      }
    } catch (error) {
      console.error("Error checking email:", error);
      setIsLoading(false);
      return false;
    }
  };

  const checkUsernameExists = async (username) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users?username=${username}`,
        {
          method: "GET",
          headers: await adminHeader,
        },
      );

      if (response.ok) {
        const data = await response.json();
        const userExists = data.some((user) => user.username === username);
        return userExists;
      } else {
        console.error("Failed to fetch data:", response.statusText);
        return false;
      }
    } catch (error) {
      console.error("Error checking username:", error);
      return false;
    }
  };

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!regex.test(password)) {
      return "Password must have at least 8 characters, including uppercase, lowercase, numbers, and special characters.";
    }
    return "";
  };

  return (
    <GradientBackground className="home">
      <CssBaseline />
      {currentPage === "register" && (
        <FormContainer>
          <Typography variant="h4" align="center" gutterBottom>
            Register
          </Typography>
          <form>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  helperText={
                    formTouched &&
                    (!validateEmail(formData.email)
                      ? "Please enter a valid email address."
                      : isEmailPresent
                        ? "This email is already in use."
                        : "")
                  }
                  error={
                    formTouched &&
                    (!validateEmail(formData.email) || isEmailPresent)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  helperText={
                    formData.username && isUsernamePresent
                      ? "This username is already taken."
                      : ""
                  }
                  error={isUsernamePresent}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  error={!!passwordError}
                  helperText={formData.password && passwordError} // Show validation message only if password is entered
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Optional Feedback for Password */}
                {formData.password && !passwordError && (
                  <Typography
                    variant="caption"
                    style={{ color: "green", marginTop: "8px" }}
                  >
                    Password looks good!
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormContainer>
      )}

      {currentPage === "subscription" && (
        <Box>
          <Typography variant="h4" align="center" gutterBottom>
            Choose Your Subscription Plan
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <PlanCard
                selected={formData.subscription === "Basic"}
                onClick={() => handlePlanSelect("Basic")}
              >
                <Typography variant="h5" gutterBottom>
                  Basic Plan
                </Typography>
                <Typography variant="body1">₹ 499/month</Typography>
                <Typography variant="body2" color="textSecondary">
                  Ideal for individuals starting out.
                </Typography>
              </PlanCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PlanCard
                selected={formData.subscription === "Standard"}
                onClick={() => handlePlanSelect("Standard")}
              >
                <Typography variant="h5" gutterBottom>
                  Standard Plan
                </Typography>
                <Typography variant="body1">₹ 999/month</Typography>
                <Typography variant="body2" color="textSecondary">
                  Perfect for small teams and businesses.
                </Typography>
              </PlanCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PlanCard
                selected={formData.subscription === "Premium"}
                onClick={() => handlePlanSelect("Premium")}
              >
                <Typography variant="h5" gutterBottom>
                  Premium Plan
                </Typography>
                <Typography variant="body1">₹ 1999/month</Typography>
                <Typography variant="body2" color="textSecondary">
                  Best for enterprises and professionals.
                </Typography>
              </PlanCard>
            </Grid>
          </Grid>
          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleConfirm}
              disabled={!formData.subscription}
            >
              Confirm Plan
            </Button>
          </Box>
        </Box>
      )}

      <div>
        <Dialog open={isLoading}>
          <DialogTitle>Registration In Progress</DialogTitle>
          <DialogContent>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <CircularProgress />
              <Typography variant="body1" style={{ marginTop: "16px" }}>
                Please wait while we complete your registration...
              </Typography>
            </div>
          </DialogContent>
          <DialogActions>
            {/* Optionally, add a Cancel button if needed */}
            {/* <Button onClick={handleClose} color="primary">Cancel</Button> */}
          </DialogActions>
        </Dialog>
      </div>
    </GradientBackground>
  );
};

export default App;
